import { useState, useEffect, useCallback, useMemo, useRef, type ChangeEvent } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormGroup,
  IconButton,
  LinearProgress,
  MenuItem,
  OutlinedInput,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  Alert,
  Checkbox,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CheckIcon from '@mui/icons-material/Check';
import BoltIcon from '@mui/icons-material/Bolt';
import TuneIcon from '@mui/icons-material/Tune';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import DownloadIcon from '@mui/icons-material/Download';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import TranslateIcon from '@mui/icons-material/Translate';
import type { Brand, BrandQuery, BrandQueryAxis } from '@traaker/shared';
import {
  BRAND_QUERY_TEXT_MAX_CHARS,
  resolveEntitlement,
  estimateAuditCost,
  type AuditType,
} from '@traaker/shared';
import { brandQueriesApi, type GeneratedQueryProposal } from '../../services/brand-queries';
import { EngineLogo } from './EngineLogo';
import { Flag, ZoneFlag } from '../../pages/dashboard/Flag';
import { resolveZone, languageFlag } from '../../pages/dashboard/geoFlags';
import { api } from '../../services/api';
import { formatNumber } from '../../i18n/format';
import { useAuth } from '../../contexts/AuthContext';
import { useCredits } from '../../contexts/CreditContext';
import { useBrand } from '../../contexts/BrandContext';

const AXIS_LABEL_KEYS: Record<BrandQueryAxis, string> = {
  narrative: 'queryManager.axis.narrative',
  authority: 'queryManager.axis.authority',
  referral: 'queryManager.axis.referral',
};
const axisLabel = (t: TFunction, axis: BrandQueryAxis): string => t(AXIS_LABEL_KEYS[axis]);

// Source-group editor label keys per axis (authority topics / referral use cases).
const TOPIC_EDITOR_LABEL_KEYS: Record<
  BrandQueryAxis,
  { button: string; title: string; field: string }
> = {
  narrative: { button: '', title: '', field: '' },
  authority: {
    button: 'queryManager.topicEditor.authority.button',
    title: 'queryManager.topicEditor.authority.title',
    field: 'queryManager.topicEditor.authority.field',
  },
  referral: {
    button: 'queryManager.topicEditor.referral.button',
    title: 'queryManager.topicEditor.referral.title',
    field: 'queryManager.topicEditor.referral.field',
  },
};

const LEVEL_LABELS: Record<string, string> = {
  L1: 'L1',
  L2: 'L2',
  L3: 'L3',
  L4: 'L4',
  L5: 'L5',
  pending: '…',
};

// Engine ids in the run-path convention (NOT the registry's `chatgpt`).
// `secondary` = simulated engine (a model + a web-search harness we own),
// labeled "indicatif".
// Array order = left-to-right display order; AI Overview leads.
const ALL_ENGINES: { id: string; label: string; secondary?: boolean }[] = [
  { id: 'ai_overview', label: 'AI Overview', secondary: true },
  { id: 'perplexity', label: 'Perplexity' },
  { id: 'openai', label: 'ChatGPT' },
  { id: 'gemini', label: 'Gemini' },
  { id: 'claude', label: 'Claude' },
  { id: 'mistral', label: 'Mistral', secondary: true },
];

// The engines each axis actually probes (mirrors orchestrator.ENGINES_BY_AXIS).
// All three axes probe all six: ChatGPT was authority-excluded while it returned
// no citations and joined on 2026-08-28 with OpenAI's `web_search` tool;
// `ai_overview` joined all three on 2026-09-03 (it always searches, so its
// citation set is unconditionally real).
const AXIS_ENGINE_ROSTER: Record<BrandQueryAxis, string[]> = {
  narrative: ['perplexity', 'openai', 'gemini', 'claude', 'mistral', 'ai_overview'],
  authority: ['perplexity', 'openai', 'gemini', 'claude', 'mistral', 'ai_overview'],
  referral: ['perplexity', 'openai', 'gemini', 'claude', 'mistral', 'ai_overview'],
};

// Default selection when the brand has none saved = PRIMARY engines only.
// Secondary engines are explicit opt-in (never auto-enabled / auto-billed).
const DEFAULT_ENGINE_IDS = ALL_ENGINES.filter((e) => !e.secondary).map((e) => e.id);

const AUDIT_TYPE_BY_AXIS: Record<BrandQueryAxis, AuditType> = {
  narrative: 'brand_narrative',
  authority: 'brand_authority',
  referral: 'brand_referral',
};

interface QueryManagerProps {
  brand: Brand;
  axis: BrandQueryAxis;
  onChanged?: () => void;
}

type ZoneMeta = { countryCode?: string; scale?: string };

/** Language flag + uppercase code (falls back to a globe when unmapped). */
function LangFlag({ code, size = 20 }: { code: string; size?: number }) {
  const cc = languageFlag(code);
  return (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
      <Flag countryCode={cc} size={size} title={code.toUpperCase()} />
      <Typography variant="caption" fontWeight={600}>
        {code.toUpperCase()}
      </Typography>
    </Box>
  );
}

/** Zone flag/icon (dashboard style) resolved from the brand's stored location meta. */
function ZoneChip({
  zone,
  storedByZone,
  size = 20,
}: {
  zone: string;
  storedByZone: Map<string, ZoneMeta>;
  size?: number;
}) {
  const resolved = resolveZone(zone, storedByZone.get(zone.toLowerCase()));
  return (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
      <ZoneFlag zone={resolved} size={size} title={zone} />
      <Typography variant="caption">{zone}</Typography>
    </Box>
  );
}

export default function QueryManager({ brand, axis, onChanged }: QueryManagerProps) {
  const { t } = useTranslation();
  const { tenant } = useAuth();
  const { isAgency, balance } = useCredits();
  const { refreshBrands } = useBrand();

  // `allQueries` keeps both active + archived so filters can include archived;
  // `queries` (computed) is the post-filter view rendered in the table.
  const [allQueries, setAllQueries] = useState<BrandQuery[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editing, setEditing] = useState<BrandQuery | null>(null);
  const [creating, setCreating] = useState(false);
  const [regenOpen, setRegenOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [topicsOpen, setTopicsOpen] = useState(false);
  const [translating, setTranslating] = useState<BrandQuery | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [bulkArchiving, setBulkArchiving] = useState(false);

  // Tenant-wide `prompts` gauge (the real enforced cap, across all brands+axes).
  const [promptsUsed, setPromptsUsed] = useState<number | null>(null);

  // Per-brand engine allowlist (agency only). Defaults to the full roster when
  // unset on the brand. The stored value lives on `brand.auditConfig`, which can
  // arrive/refresh after first mount — re-sync to it (the useState initializer
  // only runs once, so without this effect the checkboxes stay all-checked and
  // never reflect what was saved).
  const savedEngines = brand.auditConfig?.enginesEnabled;
  const [enginesEnabled, setEnginesEnabled] = useState<string[]>(
    savedEngines && savedEngines.length > 0 ? savedEngines : DEFAULT_ENGINE_IDS,
  );
  const [savingEngines, setSavingEngines] = useState(false);

  useEffect(() => {
    setEnginesEnabled(
      savedEngines && savedEngines.length > 0 ? savedEngines : DEFAULT_ENGINE_IDS,
    );
    // Key on the serialized stored value so a refresh that re-binds the brand
    // object (new reference, same content) doesn't clobber an in-progress edit.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(savedEngines)]);

  // Filters
  const [statusFilter, setStatusFilter] = useState<'active' | 'archived' | 'all'>('active');
  const [levelFilter, setLevelFilter] = useState<string>('all');
  const [zoneFilter, setZoneFilter] = useState<string>('all');
  const [languageFilter, setLanguageFilter] = useState<string>('all');

  const zones = Array.from(
    new Set(
      (brand.locations || []).map((l) => l.zone).filter(Boolean) as string[],
    ),
  );
  // zone label (lowercased) → stored country/scale, so ZoneFlag renders the
  // exact flag/icon persisted at onboarding instead of re-inferring.
  const storedByZone = useMemo(() => {
    const m = new Map<string, ZoneMeta>();
    for (const l of brand.locations || []) {
      if (l.zone) m.set(l.zone.toLowerCase(), { countryCode: l.countryCode, scale: l.scale });
    }
    return m;
  }, [brand.locations]);
  const languages = Array.from(
    new Set(
      ((brand.strategy?.targetLanguages || []) as string[])
        .map((l) => (l || '').trim().toLowerCase())
        .filter(Boolean),
    ),
  );

  // Tenant-wide prompts entitlement (limit; -1 = unlimited).
  const promptsLimit = useMemo(() => {
    if (!tenant?.tier) return -1;
    return resolveEntitlement('prompts', tenant.tier, {
      override: (tenant.settings?.quotaOverrides as any)?.prompts,
      packs: tenant.settings?.activePacks?.prompts,
    }).limit;
  }, [tenant?.tier, tenant?.settings?.quotaOverrides, tenant?.settings?.activePacks]);

  const remainingQuota =
    promptsLimit === -1 || promptsUsed === null
      ? Infinity
      : Math.max(0, promptsLimit - promptsUsed);
  const quotaFull = remainingQuota <= 0;

  // Source-group list for this axis (authority topics / referral use cases),
  // used by both the generate selector and the topics editor.
  const topicSource = useMemo(() => {
    if (axis === 'authority') return brand.strategy?.keywords || [];
    if (axis === 'referral') return brand.strategy?.scenarios || [];
    return [];
  }, [axis, brand.strategy?.keywords, brand.strategy?.scenarios]);

  const loadUsage = useCallback(async () => {
    try {
      const usage = await api.get<{ metrics: { metric: string; used: number }[] }>('/metering/usage');
      const row = usage.metrics?.find((m) => m.metric === 'prompts');
      if (row) setPromptsUsed(row.used);
    } catch {
      // Non-critical — the header just won't show used/limit.
    }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await brandQueriesApi.list(brand.brandId, axis);
      setAllQueries(data);
      setSelected(new Set());
      setError('');
    } catch (err: any) {
      setError(err?.message || t('queryManager.error.load'));
    } finally {
      setLoading(false);
    }
  }, [brand.brandId, axis, t]);

  const queries = allQueries
    .filter((q) => statusFilter === 'all' || q.status === statusFilter)
    .filter((q) => levelFilter === 'all' || (q.level || '') === levelFilter)
    .filter((q) => zoneFilter === 'all' || (q.zone || '__none__') === zoneFilter)
    .filter((q) => languageFilter === 'all' || (q.language || '__none__') === languageFilter);

  const handleBulkArchive = async () => {
    if (selected.size === 0 || bulkArchiving) return;
    setBulkArchiving(true);
    setError('');
    try {
      for (const queryId of selected) {
        await brandQueriesApi.archive(brand.brandId, axis, queryId);
      }
      await load();
      await loadUsage();
      onChanged?.();
    } catch (err: any) {
      setError(err?.message || t('queryManager.error.bulkArchive'));
    } finally {
      setBulkArchiving(false);
    }
  };

  const allSelected = queries.length > 0 && selected.size === queries.length;
  const someSelected = selected.size > 0 && !allSelected;
  const toggleAll = () => {
    setSelected(allSelected ? new Set() : new Set(queries.map((q) => q.queryId)));
  };
  const toggleOne = (queryId: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(queryId)) next.delete(queryId);
      else next.add(queryId);
      return next;
    });
  };

  useEffect(() => {
    load();
    loadUsage();
  }, [load, loadUsage]);

  const handleArchive = async (q: BrandQuery) => {
    try {
      await brandQueriesApi.archive(brand.brandId, axis, q.queryId);
      await load();
      await loadUsage();
      onChanged?.();
    } catch (err: any) {
      setError(err?.message || t('queryManager.error.archive'));
    }
  };

  // ── Agency: engine allowlist + next-audit cost ──
  const activeCount = allQueries.filter((q) => q.status === 'active').length;
  const competitorCount = (brand.competitors || []).length;
  const selectedAxisEngineCount = AXIS_ENGINE_ROSTER[axis].filter((e) =>
    enginesEnabled.includes(e),
  ).length;
  const costEstimate = useMemo(() => {
    if (!isAgency) return null;
    return estimateAuditCost(AUDIT_TYPE_BY_AXIS[axis], {
      promptCount: activeCount,
      engineCount: selectedAxisEngineCount,
      competitorCount,
    });
  }, [isAgency, axis, activeCount, selectedAxisEngineCount, competitorCount]);

  const toggleEngine = (id: string) => {
    setEnginesEnabled((prev) =>
      prev.includes(id) ? prev.filter((e) => e !== id) : [...prev, id],
    );
  };

  const saveEngines = async () => {
    if (savingEngines) return;
    setSavingEngines(true);
    setError('');
    try {
      await api.patch(`/brands/${brand.brandId}/audit-config`, { enginesEnabled });
      // Refresh the brand list so the `brand` prop reflects the saved selection
      // (resets `enginesDirty`); the sync effect re-binds the checkboxes to it.
      await refreshBrands();
      onChanged?.();
    } catch (err: any) {
      setError(err?.message || t('queryManager.error.saveEngines'));
    } finally {
      setSavingEngines(false);
    }
  };

  const enginesDirty = useMemo(() => {
    const current = new Set(
      brand.auditConfig?.enginesEnabled && brand.auditConfig.enginesEnabled.length > 0
        ? brand.auditConfig.enginesEnabled
        : DEFAULT_ENGINE_IDS,
    );
    if (current.size !== enginesEnabled.length) return true;
    return enginesEnabled.some((e) => !current.has(e));
  }, [brand.auditConfig?.enginesEnabled, enginesEnabled]);

  return (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        {/* Tenant-wide prompts quota (the real enforced cap, all brands + axes). */}
        {promptsLimit !== -1 && (
          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
              <Typography variant="caption" color="text.secondary">
                {t('queryManager.promptsUsed')}
              </Typography>
              <Typography variant="caption" color={quotaFull ? 'error' : 'text.secondary'} fontWeight={600}>
                {promptsUsed ?? '…'} / {promptsLimit}
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={promptsUsed === null ? 0 : Math.min(100, (promptsUsed / promptsLimit) * 100)}
              color={quotaFull ? 'error' : 'primary'}
              sx={{ height: 6, borderRadius: 3 }}
            />
          </Box>
        )}

        {/* Agency: per-brand model selection + next-audit cost. */}
        {isAgency && (
          <Box sx={{ mb: 2, p: 1.5, bgcolor: 'action.hover', borderRadius: 1 }}>
            <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
              {t('queryManager.enginesEnabledTitle')}
            </Typography>
            <FormGroup row sx={{ mb: 1 }}>
              {ALL_ENGINES.map((e) => {
                const selected = enginesEnabled.includes(e.id);
                return (
                  <Tooltip key={e.id} title={e.label}>
                    <Box
                      onClick={() => toggleEngine(e.id)}
                      role="checkbox"
                      aria-checked={selected}
                      aria-label={e.label}
                      sx={{
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 40,
                        height: 40,
                        borderRadius: 1.5,
                        border: (theme) =>
                          `2px solid ${selected ? theme.palette.primary.main : 'rgba(11,11,15,0.12)'}`,
                        bgcolor: selected ? 'action.selected' : 'transparent',
                        opacity: selected ? 1 : 0.5,
                        transition: 'all 0.12s',
                        '&:hover': { opacity: 1, borderColor: 'primary.light' },
                      }}
                    >
                      <EngineLogo engine={e.id} size={22} />
                    </Box>
                  </Tooltip>
                );
              })}
            </FormGroup>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              {costEstimate && costEstimate.total > 0 && (
                <Tooltip
                  title={t('queryManager.costTooltip', {
                    prompts: activeCount,
                    engines: selectedAxisEngineCount,
                    competitorsPart:
                      competitorCount > 0
                        ? t('queryManager.costTooltipCompetitors', { count: competitorCount })
                        : '',
                    axis: axisLabel(t, axis),
                  })}
                >
                  <Chip
                    icon={<BoltIcon fontSize="small" />}
                    size="small"
                    variant="outlined"
                    color={balance && balance.available < costEstimate.total ? 'warning' : 'default'}
                    label={t('queryManager.creditsPerAudit', { count: formatNumber(costEstimate.total) })}
                    sx={{ fontWeight: 600 }}
                  />
                </Tooltip>
              )}
              {enginesEnabled.length === 0 && (
                <Typography variant="caption" color="error">
                  {t('queryManager.selectAtLeastOne')}
                </Typography>
              )}
              <Box sx={{ flex: 1 }} />
              {enginesDirty && (
                <Button
                  size="small"
                  variant="contained"
                  disabled={savingEngines || enginesEnabled.length === 0}
                  onClick={saveEngines}
                  startIcon={savingEngines ? <CircularProgress size={14} color="inherit" /> : undefined}
                >
                  {t('queryManager.saveEngines')}
                </Button>
              )}
            </Box>
          </Box>
        )}

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, flexWrap: 'wrap' }}>
          <Typography variant="h6" fontWeight={600} sx={{ flex: 1 }}>
            {t('queryManager.queriesTitle', { axis: axisLabel(t, axis), count: activeCount })}
          </Typography>
          {selected.size > 0 ? (
            <>
              <Typography variant="body2" color="text.secondary">
                {t('queryManager.selectedCount', { count: selected.size })}
              </Typography>
              <Button size="small" onClick={() => setSelected(new Set())} disabled={bulkArchiving}>
                {t('common.cancel')}
              </Button>
              <Button
                size="small"
                color="error"
                variant="contained"
                startIcon={
                  bulkArchiving ? <CircularProgress size={14} color="inherit" /> : <DeleteOutlineIcon />
                }
                disabled={bulkArchiving}
                onClick={handleBulkArchive}
              >
                {t('queryManager.archiveCount', { count: selected.size })}
              </Button>
            </>
          ) : (
            <>
              {axis !== 'narrative' && (
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<TuneIcon />}
                  onClick={() => setTopicsOpen(true)}
                >
                  {t(TOPIC_EDITOR_LABEL_KEYS[axis].button)}
                </Button>
              )}
              <Button
                size="small"
                variant="outlined"
                startIcon={<UploadFileIcon />}
                onClick={() => setImportOpen(true)}
                disabled={quotaFull}
              >
                {t('queryManager.import.button')}
              </Button>
              <Button
                size="small"
                variant="outlined"
                startIcon={<AutoAwesomeIcon />}
                onClick={() => setRegenOpen(true)}
                disabled={quotaFull}
              >
                {t('queryManager.generateWithAi')}
              </Button>
              <Tooltip title={quotaFull ? t('queryManager.quotaReached') : ''}>
                <span>
                  <Button
                    size="small"
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => setCreating(true)}
                    disabled={quotaFull}
                  >
                    {t('common.add')}
                  </Button>
                </span>
              </Tooltip>
            </>
          )}
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {/* Filter bar — status, level (referral only), zone (when configured), language */}
        <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap' }}>
          <Select
            size="small"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as 'active' | 'archived' | 'all')}
            sx={{ minWidth: 140 }}
          >
            <MenuItem value="active">{t('queryManager.filter.statusActive')}</MenuItem>
            <MenuItem value="archived">{t('queryManager.filter.statusArchived')}</MenuItem>
            <MenuItem value="all">{t('queryManager.filter.statusAll')}</MenuItem>
          </Select>
          {axis === 'referral' && (
            <Select
              size="small"
              value={levelFilter}
              onChange={(e) => setLevelFilter(e.target.value)}
              sx={{ minWidth: 130 }}
            >
              <MenuItem value="all">{t('queryManager.filter.levelAll')}</MenuItem>
              {(['L1', 'L2', 'L3', 'L4', 'L5', 'pending'] as const).map((lvl) => (
                <MenuItem key={lvl} value={lvl}>
                  {lvl === 'pending'
                    ? t('queryManager.filter.levelPending')
                    : t('queryManager.filter.level', { level: lvl })}
                </MenuItem>
              ))}
            </Select>
          )}
          {zones.length > 0 && (
            <Select
              size="small"
              value={zoneFilter}
              onChange={(e) => setZoneFilter(e.target.value)}
              sx={{ minWidth: 140 }}
            >
              <MenuItem value="all">{t('queryManager.filter.zoneAll')}</MenuItem>
              {zones.map((z) => (
                <MenuItem key={z} value={z}>
                  <ZoneChip zone={z} storedByZone={storedByZone} />
                </MenuItem>
              ))}
              <MenuItem value="__none__">{t('queryManager.filter.zoneNone')}</MenuItem>
            </Select>
          )}
          {languages.length > 0 && (
            <Select
              size="small"
              value={languageFilter}
              onChange={(e) => setLanguageFilter(e.target.value)}
              sx={{ minWidth: 140 }}
            >
              <MenuItem value="all">{t('queryManager.filter.languageAll')}</MenuItem>
              {languages.map((l) => (
                <MenuItem key={l} value={l}>
                  <LangFlag code={l} />
                </MenuItem>
              ))}
              <MenuItem value="__none__">{t('queryManager.filter.languageNone')}</MenuItem>
            </Select>
          )}
        </Stack>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={24} />
          </Box>
        ) : queries.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
            {t('queryManager.emptyList')}
          </Typography>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox
                      size="small"
                      checked={allSelected}
                      indeterminate={someSelected}
                      onChange={toggleAll}
                    />
                  </TableCell>
                  <TableCell>{t('queryManager.column.query')}</TableCell>
                  {axis === 'referral' && <TableCell width={80}>{t('queryManager.column.level')}</TableCell>}
                  {axis === 'referral' && <TableCell width={140}>{t('queryManager.column.zone')}</TableCell>}
                  {languages.length > 0 && <TableCell width={80}>{t('queryManager.column.language')}</TableCell>}
                  <TableCell width={120}>{t('queryManager.column.source')}</TableCell>
                  <TableCell width={100} align="right" />
                </TableRow>
              </TableHead>
              <TableBody>
                {queries.map((q) => (
                  <TableRow key={q.queryId} hover selected={selected.has(q.queryId)}>
                    <TableCell padding="checkbox">
                      <Checkbox
                        size="small"
                        checked={selected.has(q.queryId)}
                        onChange={() => toggleOne(q.queryId)}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{q.text}</Typography>
                    </TableCell>
                    {axis === 'referral' && (
                      <TableCell>
                        {q.level && q.level !== null ? (
                          <Chip label={LEVEL_LABELS[q.level] || q.level} size="small" />
                        ) : (
                          '—'
                        )}
                      </TableCell>
                    )}
                    {axis === 'referral' && (
                      <TableCell>
                        {q.zone ? (
                          <ZoneChip zone={q.zone} storedByZone={storedByZone} />
                        ) : (
                          <Typography variant="caption">—</Typography>
                        )}
                      </TableCell>
                    )}
                    {languages.length > 0 && (
                      <TableCell>
                        {q.language ? (
                          <Flag countryCode={languageFlag(q.language)} size={20} title={q.language.toUpperCase()} />
                        ) : (
                          <Typography variant="caption" color="text.secondary">
                            —
                          </Typography>
                        )}
                      </TableCell>
                    )}
                    <TableCell>
                      <Chip
                        label={
                          q.source === 'user_added'
                            ? t('queryManager.source.manual')
                            : q.source === 'user_edited'
                              ? t('queryManager.source.edited')
                              : t('queryManager.source.geo')
                        }
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title={t('common.edit')}>
                        <IconButton size="small" onClick={() => setEditing(q)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      {languages.length > 1 && (
                        <Tooltip title="Générer dans d’autres langues">
                          <IconButton
                            size="small"
                            onClick={() => setTranslating(q)}
                            disabled={quotaFull}
                          >
                            <TranslateIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      <Tooltip title={t('queryManager.archiveTooltip')}>
                        <IconButton size="small" onClick={() => handleArchive(q)}>
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </CardContent>

      <QueryFormDialog
        open={creating}
        axis={axis}
        zones={zones}
        languages={languages}
        onClose={() => setCreating(false)}
        onSubmit={async (values) => {
          await brandQueriesApi.create(brand.brandId, { axis, ...values });
          setCreating(false);
          await load();
          await loadUsage();
          onChanged?.();
        }}
      />

      <QueryFormDialog
        open={!!editing}
        axis={axis}
        zones={zones}
        languages={languages}
        initial={editing ?? undefined}
        onClose={() => setEditing(null)}
        onSubmit={async (values) => {
          if (!editing) return;
          await brandQueriesApi.update(brand.brandId, editing.queryId, {
            axis,
            text: values.text,
            zone: values.zone,
            language: values.language,
          });
          setEditing(null);
          await load();
          onChanged?.();
        }}
      />

      <RegenerateDialog
        open={regenOpen}
        brandId={brand.brandId}
        axis={axis}
        zones={zones}
        languages={languages}
        topics={topicSource}
        isAgency={isAgency}
        existingHashes={new Set(allQueries.filter((q) => q.status === 'active').map((q) => q.textHash))}
        onClose={() => setRegenOpen(false)}
        onApplied={async () => {
          setRegenOpen(false);
          await load();
          await loadUsage();
          onChanged?.();
        }}
      />

      <ImportDialog
        open={importOpen}
        brand={brand}
        zones={zones}
        languages={languages}
        remainingQuota={remainingQuota}
        onClose={() => setImportOpen(false)}
        onApplied={async () => {
          setImportOpen(false);
          await load();
          await loadUsage();
          onChanged?.();
        }}
      />

      {axis !== 'narrative' && (
        <TopicsDialog
          open={topicsOpen}
          brandId={brand.brandId}
          axis={axis}
          initial={topicSource}
          onClose={() => setTopicsOpen(false)}
          onSaved={async () => {
            setTopicsOpen(false);
            onChanged?.();
          }}
        />
      )}

      <TranslateDialog
        open={!!translating}
        brandId={brand.brandId}
        axis={axis}
        query={translating}
        languages={languages}
        remainingQuota={remainingQuota}
        onClose={() => setTranslating(null)}
        onApplied={async () => {
          setTranslating(null);
          await load();
          await loadUsage();
          onChanged?.();
        }}
      />
    </Card>
  );
}

interface QueryFormValues {
  text: string;
  level?: 'L1' | 'L2' | 'L3' | 'L4' | 'L5' | 'pending';
  zone?: string;
  language?: string;
}

function QueryFormDialog({
  open,
  axis,
  zones,
  languages,
  initial,
  onClose,
  onSubmit,
}: {
  open: boolean;
  axis: BrandQueryAxis;
  zones: string[];
  languages: string[];
  initial?: BrandQuery;
  onClose: () => void;
  onSubmit: (values: QueryFormValues) => Promise<void>;
}) {
  const { t } = useTranslation();
  const [text, setText] = useState('');
  const [zone, setZone] = useState<string>('');
  const [language, setLanguage] = useState<string>('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  // The language is REQUIRED when the brand has configured target languages
  // (so the translate-on-save rewrite has a target). Default to the first one.
  useEffect(() => {
    if (open) {
      setText(initial?.text || '');
      setZone(initial?.zone || '');
      setLanguage(initial?.language || languages[0] || '');
      setError('');
    }
  }, [open, initial, languages]);

  const handleSubmit = async () => {
    if (!text.trim()) {
      setError(t('queryManager.form.textRequired'));
      return;
    }
    if (text.length > BRAND_QUERY_TEXT_MAX_CHARS) {
      setError(t('queryManager.form.maxChars', { max: BRAND_QUERY_TEXT_MAX_CHARS }));
      return;
    }
    if (languages.length > 0 && !language) {
      setError(t('queryManager.form.chooseLanguage'));
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      await onSubmit({
        text: text.trim(),
        zone: axis === 'referral' ? zone || undefined : undefined,
        language: language || undefined,
      });
    } catch (err: any) {
      setError(err?.message || t('common.error'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{initial ? t('queryManager.form.editTitle') : t('queryManager.form.newTitle')}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {error && <Alert severity="error">{error}</Alert>}
          <TextField
            label={t('queryManager.form.textLabel')}
            value={text}
            onChange={(e) => setText(e.target.value)}
            multiline
            rows={3}
            fullWidth
            helperText={`${text.length} / ${BRAND_QUERY_TEXT_MAX_CHARS}`}
            inputProps={{ maxLength: BRAND_QUERY_TEXT_MAX_CHARS }}
          />
          {axis === 'referral' && zones.length > 0 && (
            <Select
              value={zone}
              onChange={(e) => setZone(e.target.value)}
              displayEmpty
              fullWidth
              size="small"
            >
              <MenuItem value="">{t('queryManager.form.noZone')}</MenuItem>
              {zones.map((z) => (
                <MenuItem key={z} value={z}>
                  <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                    <ZoneFlag zone={resolveZone(z)} size={18} title={z} />
                    {z}
                  </Box>
                </MenuItem>
              ))}
            </Select>
          )}
          {languages.length > 0 && (
            <Select
              value={language}
              onChange={(e) => setLanguage(e.target.value)}
              displayEmpty
              fullWidth
              size="small"
            >
              <MenuItem value="" disabled>
                {t('queryManager.form.targetLanguage')}
              </MenuItem>
              {languages.map((l) => (
                <MenuItem key={l} value={l}>
                  <LangFlag code={l} />
                </MenuItem>
              ))}
            </Select>
          )}
          <Typography variant="caption" color="text.secondary">
            {axis === 'referral'
              ? t('queryManager.form.rewriteHintReferral')
              : t('queryManager.form.rewriteHint')}
          </Typography>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={submitting}>
          {t('common.cancel')}
        </Button>
        <Button variant="contained" onClick={handleSubmit} disabled={submitting}>
          {submitting ? <CircularProgress size={20} /> : t('common.save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function RegenerateDialog({
  open,
  brandId,
  axis,
  zones,
  languages,
  topics,
  isAgency,
  existingHashes,
  onClose,
  onApplied,
}: {
  open: boolean;
  brandId: string;
  axis: BrandQueryAxis;
  zones: string[];
  languages: string[];
  topics: string[];
  isAgency: boolean;
  existingHashes: Set<string>;
  onClose: () => void;
  onApplied: () => Promise<void>;
}) {
  const { t } = useTranslation();
  // Two phases: 'config' (pick count/lang/zone/topics) → 'review' (pick rows).
  const [phase, setPhase] = useState<'config' | 'review'>('config');
  const [count, setCount] = useState(10);
  const [maxAllowed, setMaxAllowed] = useState(50);
  const [selLanguages, setSelLanguages] = useState<string[]>([]);
  const [selZones, setSelZones] = useState<string[]>([]);
  const [selTopics, setSelTopics] = useState<string[]>([]);

  const [loading, setLoading] = useState(false);
  const [proposals, setProposals] = useState<GeneratedQueryProposal[]>([]);
  const [geoStrategyHash, setGeoStrategyHash] = useState<string>('');
  const [selected, setSelected] = useState<Record<string, boolean>>({});
  const [error, setError] = useState('');
  const [applying, setApplying] = useState(false);
  const [applyErrors, setApplyErrors] = useState<Array<{ text: string; error: string }>>([]);

  // Defaults are read from refs so the reset effect can depend ONLY on `open`
  // (and `axis`). `languages`/`zones`/`topics` are arrays recreated on every
  // parent render — including background ones (e.g. the notifications poll) — so
  // keying the effect on them re-ran it mid-edit and clobbered the user's zone
  // selection. Refs let us use the latest values without retriggering.
  const defaultsRef = useRef({ languages, zones, topics });
  defaultsRef.current = { languages, zones, topics };

  // Reset to config phase, defaulting all the multi-selects, only when OPENED.
  useEffect(() => {
    if (!open) return;
    const d = defaultsRef.current;
    setPhase('config');
    setCount(10);
    setMaxAllowed(50);
    setSelLanguages(d.languages);
    setSelZones(axis === 'referral' ? d.zones : []);
    setSelTopics(d.topics);
    setProposals([]);
    setSelected({});
    setApplyErrors([]);
    setError('');
  }, [open, axis]);

  const handleGenerate = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await brandQueriesApi.regenerate(brandId, axis, {
        count,
        languages: selLanguages.length > 0 ? selLanguages : undefined,
        zones: axis === 'referral' && selZones.length > 0 ? selZones : undefined,
        topics: axis !== 'narrative' && selTopics.length > 0 ? selTopics : undefined,
      });
      const fresh = res.proposals.filter((p) => !existingHashes.has(p.textHash));
      setProposals(fresh);
      setGeoStrategyHash(res.geoStrategyHash);
      setMaxAllowed(res.maxAllowed);
      // Pre-check up to the remaining quota so the user can't select more than
      // they can apply.
      const initSel: Record<string, boolean> = {};
      let allowed = res.maxAllowed;
      for (const p of fresh) {
        initSel[p.textHash] = allowed > 0;
        if (allowed > 0) allowed -= 1;
      }
      setSelected(initSel);
      setPhase('review');
    } catch (err: any) {
      setError(err?.message || t('queryManager.regen.generateError'));
    } finally {
      setLoading(false);
    }
  };

  const selectedCount = Object.values(selected).filter(Boolean).length;
  const overQuota = selectedCount > maxAllowed;

  const handleApply = async () => {
    const items = proposals
      .filter((p) => selected[p.textHash])
      .map((p) => ({ text: p.text, level: p.level, zone: p.zone, language: p.language, topic: p.topic }));
    if (items.length === 0) return;
    setApplying(true);
    setApplyErrors([]);
    try {
      const res = await brandQueriesApi.applyRegen(brandId, { axis, geoStrategyHash, items });
      if (res.errors.length > 0) setApplyErrors(res.errors);
      if (res.created.length > 0) {
        await onApplied();
      } else if (res.errors.length === 0) {
        onClose();
      }
    } catch (err: any) {
      setError(err?.message || t('common.error'));
    } finally {
      setApplying(false);
    }
  };

  const toggleMulti = (
    value: string,
    list: string[],
    setList: (v: string[]) => void,
  ) => {
    setList(list.includes(value) ? list.filter((v) => v !== value) : [...list, value]);
  };

  const canGenerate =
    count > 0 &&
    (languages.length === 0 || selLanguages.length > 0) &&
    (axis !== 'referral' || zones.length === 0 || selZones.length > 0) &&
    (axis === 'narrative' || topics.length === 0 || selTopics.length > 0);

  // Agency-only informational hint: the number of prompts that yields ONE prompt
  // per distribution cell, so coverage is balanced across everything the user
  // selected. Referral has 5 ladder levels × zones × languages; narrative /
  // authority have no level/zone combo dimension, so the cell count is just the
  // languages. Reacts live to the chip selections. Multiples of this stay
  // balanced (the generator distributes round-robin across cells).
  const LADDER_LEVELS = 5;
  const langCells = Math.max(1, (selLanguages.length || languages.length) || 1);
  const zoneCells = Math.max(1, (selZones.length || zones.length) || 1);
  const recommendedCount =
    axis === 'referral' ? LADDER_LEVELS * zoneCells * langCells : langCells;
  // Only worth surfacing when it's > 1 (a single-language narrative brand has a
  // trivially-1 recommendation) and within the per-generation ceiling.
  const showRecommendation = isAgency && recommendedCount > 1;

  return (
    <Dialog open={open} onClose={applying ? undefined : onClose} maxWidth="md" fullWidth>
      <DialogTitle>{t('queryManager.regen.title')}</DialogTitle>
      <DialogContent>
        {phase === 'config' ? (
          <Stack spacing={2.5} sx={{ mt: 1 }}>
            {error && <Alert severity="error">{error}</Alert>}
            <Box>
              <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                {t('queryManager.regen.countLabel')}
              </Typography>
              <Stack direction="row" spacing={1.5} alignItems="flex-start">
                <TextField
                  type="number"
                  size="small"
                  value={count}
                  onChange={(e) => {
                    const n = Math.max(1, Math.min(50, Math.floor(Number(e.target.value) || 0)));
                    setCount(n);
                  }}
                  inputProps={{ min: 1, max: 50 }}
                  sx={{ width: 120 }}
                  helperText={t('queryManager.regen.countHelper')}
                />
                {showRecommendation && (
                  <Tooltip
                    title={
                      axis === 'referral'
                        ? t('queryManager.regen.recommendedTooltipReferral', {
                            levels: LADDER_LEVELS,
                            zones: zoneCells,
                            languages: langCells,
                          })
                        : t('queryManager.regen.recommendedTooltipFlat', { languages: langCells })
                    }
                  >
                    <Chip
                      size="small"
                      variant="outlined"
                      color="primary"
                      icon={<AutoAwesomeIcon fontSize="small" />}
                      label={t('queryManager.regen.recommended', {
                        count: Math.min(50, recommendedCount),
                      })}
                      onClick={() => setCount(Math.min(50, recommendedCount))}
                      sx={{ mt: 0.5, cursor: 'pointer' }}
                    />
                  </Tooltip>
                )}
              </Stack>
            </Box>

            {languages.length > 0 && (
              <Box>
                <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                  {t('queryManager.regen.languages')}
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  {languages.map((l) => (
                    <Chip
                      key={l}
                      icon={<Flag countryCode={languageFlag(l)} size={16} />}
                      label={l.toUpperCase()}
                      color={selLanguages.includes(l) ? 'primary' : 'default'}
                      variant={selLanguages.includes(l) ? 'filled' : 'outlined'}
                      onClick={() => toggleMulti(l, selLanguages, setSelLanguages)}
                      size="small"
                    />
                  ))}
                </Stack>
              </Box>
            )}

            {axis === 'referral' && zones.length > 0 && (
              <Box>
                <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                  {t('queryManager.regen.zones')}
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  {zones.map((z) => (
                    <Chip
                      key={z}
                      icon={<ZoneFlag zone={resolveZone(z)} size={16} />}
                      label={z}
                      color={selZones.includes(z) ? 'primary' : 'default'}
                      variant={selZones.includes(z) ? 'filled' : 'outlined'}
                      onClick={() => toggleMulti(z, selZones, setSelZones)}
                      size="small"
                    />
                  ))}
                </Stack>
              </Box>
            )}

            {axis !== 'narrative' && topics.length > 0 && (
              <Box>
                <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                  {axis === 'authority' ? t('queryManager.regen.themes') : t('queryManager.regen.useCases')}
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  {topics.map((t) => (
                    <Chip
                      key={t}
                      label={t}
                      color={selTopics.includes(t) ? 'primary' : 'default'}
                      variant={selTopics.includes(t) ? 'filled' : 'outlined'}
                      onClick={() => toggleMulti(t, selTopics, setSelTopics)}
                      size="small"
                    />
                  ))}
                </Stack>
              </Box>
            )}
          </Stack>
        ) : loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error">{error}</Alert>
        ) : proposals.length === 0 ? (
          <Alert severity="info">
            {t('queryManager.regen.noProposals')}
          </Alert>
        ) : (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              {t('queryManager.regen.proposalsInfo', { count: proposals.length, max: maxAllowed })}
            </Typography>
            {overQuota && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                {t('queryManager.regen.overQuota', {
                  selected: selectedCount,
                  max: maxAllowed,
                  excess: selectedCount - maxAllowed,
                })}
              </Alert>
            )}
            {applyErrors.length > 0 && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>
                  {t('queryManager.regen.rejectedCount', { count: applyErrors.length })}
                </Typography>
                {applyErrors.slice(0, 3).map((e, i) => (
                  <Typography key={i} variant="caption" display="block">
                    • {e.text.slice(0, 80)} — {e.error}
                  </Typography>
                ))}
              </Alert>
            )}
            <TableContainer component={Box} sx={{ maxHeight: 400 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell width={40} />
                    <TableCell>{t('queryManager.column.query')}</TableCell>
                    {axis === 'referral' && <TableCell width={80}>{t('queryManager.column.level')}</TableCell>}
                    {axis === 'referral' && <TableCell width={140}>{t('queryManager.column.zone')}</TableCell>}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {proposals.map((p) => (
                    <TableRow key={p.textHash} hover>
                      <TableCell padding="checkbox">
                        <Checkbox
                          size="small"
                          checked={!!selected[p.textHash]}
                          onChange={(e) =>
                            setSelected({ ...selected, [p.textHash]: e.target.checked })
                          }
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{p.text}</Typography>
                      </TableCell>
                      {axis === 'referral' && (
                        <TableCell>{p.level ? <Chip label={p.level} size="small" /> : '—'}</TableCell>
                      )}
                      {axis === 'referral' && (
                        <TableCell>
                          <Typography variant="caption">{p.zone || '—'}</Typography>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={applying}>
          {t('common.close')}
        </Button>
        {phase === 'config' ? (
          <Button
            variant="contained"
            onClick={handleGenerate}
            disabled={loading || !canGenerate}
            startIcon={loading ? <CircularProgress size={16} /> : <AutoAwesomeIcon />}
          >
            {loading ? t('queryManager.regen.generating') : t('queryManager.regen.generate')}
          </Button>
        ) : (
          <Button
            variant="contained"
            onClick={handleApply}
            disabled={applying || selectedCount === 0 || overQuota}
            startIcon={applying ? <CircularProgress size={16} /> : <CheckIcon />}
          >
            {applying ? t('queryManager.regen.applying') : t('queryManager.regen.addCount', { count: selectedCount })}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

/**
 * "Generate this prompt in other languages" — for each picked target language,
 * re-creates the query with the SAME source text + that language. The server's
 * create path localizes (rewrites) the text into the target language, so each
 * new row is a proper translation, not a copy. Each create is quota-checked
 * server-side; we stop early when the remaining quota runs out.
 */
function TranslateDialog({
  open,
  brandId,
  axis,
  query,
  languages,
  remainingQuota,
  onClose,
  onApplied,
}: {
  open: boolean;
  brandId: string;
  axis: BrandQueryAxis;
  query: BrandQuery | null;
  languages: string[];
  remainingQuota: number;
  onClose: () => void;
  onApplied: () => Promise<void>;
}) {
  const sourceLang = (query?.language || '').toLowerCase();
  const targets = languages.filter((l) => l !== sourceLang);
  const [selected, setSelected] = useState<string[]>([]);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState('');
  const [errors, setErrors] = useState<Array<{ lang: string; error: string }>>([]);

  useEffect(() => {
    if (open) {
      setSelected(targets);
      setRunning(false);
      setError('');
      setErrors([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, query?.queryId]);

  const overQuota = Number.isFinite(remainingQuota) && selected.length > remainingQuota;

  const toggle = (l: string) =>
    setSelected((prev) => (prev.includes(l) ? prev.filter((x) => x !== l) : [...prev, l]));

  const handleRun = async () => {
    if (!query || selected.length === 0) return;
    setRunning(true);
    setError('');
    const collected: Array<{ lang: string; error: string }> = [];
    for (const lang of selected) {
      try {
        await brandQueriesApi.create(brandId, {
          axis,
          text: query.text,
          zone: axis === 'referral' ? query.zone : undefined,
          language: lang,
        });
      } catch (err: any) {
        collected.push({ lang, error: err?.message || 'Erreur' });
      }
    }
    setErrors(collected);
    setRunning(false);
    if (collected.length < selected.length) {
      await onApplied();
    } else if (collected.length === 0) {
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={running ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Générer dans d’autres langues</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {error && <Alert severity="error">{error}</Alert>}
          {query && (
            <Box sx={{ p: 1.5, bgcolor: 'action.hover', borderRadius: 1 }}>
              <Typography variant="caption" color="text.secondary">
                Prompt source{sourceLang ? ` (${sourceLang.toUpperCase()})` : ''}
              </Typography>
              <Typography variant="body2">{query.text}</Typography>
            </Box>
          )}
          {targets.length === 0 ? (
            <Alert severity="info">Aucune autre langue configurée pour cette marque.</Alert>
          ) : (
            <>
              <Typography variant="subtitle2" fontWeight={600}>
                Langues cibles
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {targets.map((l) => {
                  const sel = selected.includes(l);
                  return (
                    <Chip
                      key={l}
                      icon={<Flag countryCode={languageFlag(l)} size={16} />}
                      label={l.toUpperCase()}
                      color={sel ? 'primary' : 'default'}
                      variant={sel ? 'filled' : 'outlined'}
                      onClick={() => toggle(l)}
                      size="small"
                    />
                  );
                })}
              </Stack>
              <Typography variant="caption" color="text.secondary">
                Le prompt sera réécrit dans chaque langue sélectionnée avant enregistrement.
              </Typography>
              {overQuota && (
                <Alert severity="warning">
                  {selected.length} langue(s) sélectionnée(s) — quota restant : {remainingQuota}.
                </Alert>
              )}
              {errors.length > 0 && (
                <Alert severity="warning">
                  {errors.length} langue(s) en échec :{' '}
                  {errors.map((e) => e.lang.toUpperCase()).join(', ')}
                </Alert>
              )}
            </>
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={running}>
          Fermer
        </Button>
        <Button
          variant="contained"
          onClick={handleRun}
          disabled={running || selected.length === 0 || overQuota || targets.length === 0}
          startIcon={running ? <CircularProgress size={16} /> : <TranslateIcon />}
        >
          {running ? 'Génération…' : `Générer (${selected.length})`}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/**
 * Chip-editor for the per-axis source groups (authority topics / referral use
 * cases). Saving writes `brand.strategy.keywords` / `.scenarios` and the server
 * auto re-validates Brand + GEO so audits aren't blocked.
 */
function TopicsDialog({
  open,
  brandId,
  axis,
  initial,
  onClose,
  onSaved,
}: {
  open: boolean;
  brandId: string;
  axis: BrandQueryAxis;
  initial: string[];
  onClose: () => void;
  onSaved: () => Promise<void>;
}) {
  const { t } = useTranslation();
  const [items, setItems] = useState<string[]>([]);
  const [input, setInput] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (open) {
      setItems(initial);
      setInput('');
      setError('');
    }
  }, [open, initial]);

  const add = () => {
    const v = input.trim();
    if (v && !items.some((i) => i.toLowerCase() === v.toLowerCase())) {
      setItems([...items, v]);
    }
    setInput('');
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      await brandQueriesApi.updateTopics(brandId, axis, items);
      await onSaved();
    } catch (err: any) {
      setError(err?.message || t('common.error'));
    } finally {
      setSaving(false);
    }
  };

  const label = TOPIC_EDITOR_LABEL_KEYS[axis];
  const fieldLabel = label.field ? t(label.field) : '';

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{label.title ? t(label.title) : ''}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {error && <Alert severity="error">{error}</Alert>}
          <Typography variant="caption" color="text.secondary">
            {t('queryManager.topicsDialog.hint')}
          </Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {items.map((t) => (
              <Chip
                key={t}
                label={t}
                onDelete={() => setItems(items.filter((i) => i !== t))}
                size="small"
              />
            ))}
            {items.length === 0 && (
              <Typography variant="body2" color="text.secondary">
                {t('queryManager.topicsDialog.empty', { field: fieldLabel })}
              </Typography>
            )}
          </Stack>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <OutlinedInput
              size="small"
              fullWidth
              placeholder={t('queryManager.topicsDialog.addPlaceholder', { field: fieldLabel })}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  add();
                }
              }}
            />
            <Button onClick={add} variant="outlined" disabled={!input.trim()}>
              {t('common.add')}
            </Button>
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={saving}>
          {t('common.cancel')}
        </Button>
        <Button variant="contained" onClick={handleSave} disabled={saving}>
          {saving ? <CircularProgress size={20} /> : t('common.save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ── CSV import helpers ────────────────────────────────────────────────────────

interface ImportRow {
  axis: BrandQueryAxis;
  text: string;
  zone?: string;
  language?: string;
  level?: string;
  topic?: string;
  /** Fatal validation error — row is disabled in the review table. */
  error?: string;
}

function parseCsvLine(line: string): string[] {
  const fields: string[] = [];
  let current = '';
  let inQuotes = false;
  let i = 0;
  while (i < line.length) {
    const ch = line[i];
    if (inQuotes) {
      if (ch === '"') {
        if (i + 1 < line.length && line[i + 1] === '"') {
          current += '"';
          i += 2;
          continue;
        }
        inQuotes = false;
      } else {
        current += ch;
      }
    } else if (ch === '"') {
      inQuotes = true;
    } else if (ch === ',') {
      fields.push(current.trim());
      current = '';
    } else {
      current += ch;
    }
    i++;
  }
  fields.push(current.trim());
  return fields;
}

function parseCsvToRows(
  content: string,
): { rows: ImportRow[]; parseErrors: string[] } {
  const lines = content.split(/\r?\n/);
  const rows: ImportRow[] = [];
  const parseErrors: string[] = [];
  let colIndex: Record<string, number> = {};
  let headerFound = false;

  for (const line of lines) {
    const trimmed = line.trim();
    if (!trimmed) continue;
    const fields = parseCsvLine(trimmed);
    // Skip comment rows whether the # is unquoted (plain text) or quoted by Excel
    // after opening and re-saving the template ("# comment",value1,...).
    if ((fields[0] || '').trim().startsWith('#')) continue;

    if (!headerFound) {
      fields.forEach((f, idx) => { colIndex[f.toLowerCase()] = idx; });
      headerFound = true;
      if (!('axis' in colIndex) || !('text' in colIndex)) {
        parseErrors.push('En-tête invalide : les colonnes "axis" et "text" sont requises.');
        return { rows: [], parseErrors };
      }
      continue;
    }

    const get = (col: string): string => {
      const idx = colIndex[col];
      return idx !== undefined ? (fields[idx] || '').trim() : '';
    };

    const axis = get('axis');
    const text = get('text');
    const zone = get('zone') || undefined;
    const language = get('language') || undefined;
    const rawLevel = get('level');
    const level = rawLevel && ['L1', 'L2', 'L3', 'L4', 'L5'].includes(rawLevel) ? rawLevel : undefined;
    const topic = get('topic') || undefined;

    let rowError: string | undefined;
    if (!(['narrative', 'authority', 'referral'] as string[]).includes(axis)) {
      rowError = `Axe invalide : "${axis}"`;
    } else if (!text) {
      rowError = 'Texte requis';
    } else if (text.length > BRAND_QUERY_TEXT_MAX_CHARS) {
      rowError = `Trop long (${text.length} > ${BRAND_QUERY_TEXT_MAX_CHARS})`;
    }

    rows.push({ axis: axis as BrandQueryAxis, text, zone, language, level, topic, error: rowError });
  }

  return { rows, parseErrors };
}

function buildCsvTemplate(brand: Brand, zones: string[], languages: string[]): string {
  const firstLang = languages[0] || 'fr';
  // Multi-cell comment row: label in col A, each value in its own cell (readable in Excel/Sheets).
  const row = (...cells: string[]) => cells.map((c) => (c.includes(',') || c.includes('"') || c.startsWith('#') ? `"${c.replace(/"/g, '""')}"` : c)).join(',');
  const langNote = languages.length
    ? `Pour ajouter d'autres langues, activez-les dans Brand Knowledge de la marque.`
    : `Aucune langue configurée — ajoutez-en dans Brand Knowledge.`;
  const zoneNote = zones.length
    ? `Pour ajouter des zones, ajoutez des localisations dans la marque.`
    : `Aucune zone configurée — ajoutez des localisations dans la marque.`;
  const lines = [
    row('# MODÈLE — ne modifiez pas les noms de colonnes de la ligne d\'en-tête (ligne 9).'),
    row('# Axes valides :', 'narrative', 'authority', 'referral'),
    row(`# Langues configurées pour ${brand.name} :`, ...(languages.length ? languages : ['(aucune)'])),
    row(`# ${langNote}`),
    ...(zones.length ? [row('# Zones configurées (axe referral uniquement) :', ...zones)] : []),
    row(`# ${zoneNote}`),
    row('# Niveau : laissez VIDE — l\'IA assigne automatiquement L1 à L5 après import.'),
    row('# Thème/cas d\'usage (topic) : regroupe vos requêtes par sujet. Pour authority : le thème éditorial (ex: seo). Pour referral : le cas d\'usage commercial (ex: acquisition). Laissez vide si non pertinent.'),
    ``,
    `axis,text,zone,language,level,topic`,
    `narrative,"Comment ${brand.name.replace(/"/g, '""')} est-elle perçue par les IA ?",,${firstLang},,`,
    `authority,"Quelles sont les meilleures pratiques en [thème] ?",,${firstLang},,seo`,
    `referral,"Quelle entreprise recommandez-vous pour [cas d'usage] ?",,${firstLang},,acquisition`,
  ];
  return lines.join('\n');
}

// ── ImportDialog ─────────────────────────────────────────────────────────────

function ImportDialog({
  open,
  brand,
  zones,
  languages,
  remainingQuota,
  onClose,
  onApplied,
}: {
  open: boolean;
  brand: Brand;
  zones: string[];
  languages: string[];
  remainingQuota: number;
  onClose: () => void;
  onApplied: () => Promise<void>;
}) {
  const { t } = useTranslation();
  const [phase, setPhase] = useState<'upload' | 'review'>('upload');
  const [rows, setRows] = useState<ImportRow[]>([]);
  const [parseErrors, setParseErrors] = useState<string[]>([]);
  const [selected, setSelected] = useState<Record<number, boolean>>({});
  const [applying, setApplying] = useState(false);
  const [applyErrors, setApplyErrors] = useState<Array<{ index: number; text: string; error: string }>>([]);
  const [error, setError] = useState('');
  const fileRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!open) return;
    setPhase('upload');
    setRows([]);
    setParseErrors([]);
    setSelected({});
    setApplyErrors([]);
    setError('');
  }, [open]);

  const handleDownloadTemplate = () => {
    const csv = buildCsvTemplate(brand, zones, languages);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `prompts-template-${brand.name.toLowerCase().replace(/[^a-z0-9]+/g, '-')}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleFile = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const content = (ev.target?.result as string) || '';
      const { rows: parsed, parseErrors: errs } = parseCsvToRows(content);
      setRows(parsed);
      setParseErrors(errs);
      if (parsed.length > 0) {
        const initSel: Record<number, boolean> = {};
        let quota = remainingQuota === Infinity ? 9999 : remainingQuota;
        parsed.forEach((r, i) => {
          const selectable = !r.error && quota > 0;
          initSel[i] = selectable;
          if (selectable) quota--;
        });
        setSelected(initSel);
        setPhase('review');
      }
    };
    reader.readAsText(file, 'utf-8');
    e.target.value = '';
  };

  const selectedCount = Object.values(selected).filter(Boolean).length;
  const validCount = rows.filter((r) => !r.error).length;

  const handleApply = async () => {
    const items = rows
      .filter((_, i) => selected[i])
      .map((r) => ({ axis: r.axis, text: r.text, zone: r.zone, language: r.language, level: r.level, topic: r.topic }));
    if (items.length === 0) return;
    setApplying(true);
    setApplyErrors([]);
    setError('');
    try {
      const res = await brandQueriesApi.importQueries(brand.brandId, items);
      if (res.errors.length > 0) setApplyErrors(res.errors);
      if (res.created.length > 0) {
        await onApplied();
      } else if (res.errors.length === 0) {
        onClose();
      }
    } catch (err: any) {
      setError(err?.message || t('common.error'));
    } finally {
      setApplying(false);
    }
  };

  return (
    <Dialog open={open} onClose={applying ? undefined : onClose} maxWidth="md" fullWidth>
      <DialogTitle>{t('queryManager.import.title')}</DialogTitle>
      <DialogContent>
        {phase === 'upload' ? (
          <Stack spacing={2} sx={{ mt: 1 }}>
            {error && <Alert severity="error">{error}</Alert>}
            <Alert severity="info">{t('queryManager.import.instructions')}</Alert>
            <Button
              variant="outlined"
              size="small"
              startIcon={<DownloadIcon />}
              onClick={handleDownloadTemplate}
              sx={{ alignSelf: 'flex-start' }}
            >
              {t('queryManager.import.downloadTemplate')}
            </Button>
            <Box>
              <input
                ref={fileRef}
                type="file"
                accept=".csv,text/csv"
                style={{ display: 'none' }}
                onChange={handleFile}
              />
              <Button
                variant="contained"
                startIcon={<UploadFileIcon />}
                onClick={() => fileRef.current?.click()}
              >
                {t('queryManager.import.chooseFile')}
              </Button>
            </Box>
            {parseErrors.length > 0 && (
              <Alert severity="error">
                {parseErrors.map((e, i) => <div key={i}>{e}</div>)}
              </Alert>
            )}
          </Stack>
        ) : (
          <>
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              {t('queryManager.import.reviewInfo', {
                count: validCount,
                max: remainingQuota === Infinity ? '∞' : remainingQuota,
              })}
            </Typography>
            {applyErrors.length > 0 && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>
                  {t('queryManager.import.rejectedCount', { count: applyErrors.length })}
                </Typography>
                {applyErrors.slice(0, 3).map((e, i) => (
                  <Typography key={i} variant="caption" display="block">
                    • {e.text} — {e.error}
                  </Typography>
                ))}
              </Alert>
            )}
            <TableContainer component={Box} sx={{ maxHeight: 420 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell width={40} />
                    <TableCell width={120}>{t('queryManager.import.column.axis')}</TableCell>
                    <TableCell>{t('queryManager.column.query')}</TableCell>
                    <TableCell width={110}>{t('queryManager.column.zone')}</TableCell>
                    <TableCell width={80}>{t('queryManager.column.language')}</TableCell>
                    <TableCell width={70}>{t('queryManager.column.level')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((row, i) => (
                    <TableRow key={i} hover sx={row.error ? { opacity: 0.5 } : undefined}>
                      <TableCell padding="checkbox">
                        <Tooltip title={row.error || ''}>
                          <span>
                            <Checkbox
                              size="small"
                              checked={!!selected[i]}
                              disabled={!!row.error}
                              onChange={(e) => setSelected({ ...selected, [i]: e.target.checked })}
                            />
                          </span>
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={row.axis}
                          size="small"
                          color={
                            row.axis === 'narrative' ? 'default' :
                            row.axis === 'authority' ? 'primary' : 'secondary'
                          }
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{row.text}</Typography>
                        {row.error && (
                          <Typography variant="caption" color="error">{row.error}</Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        {row.zone
                          ? <ZoneChip zone={row.zone} storedByZone={new Map()} size={16} />
                          : <Typography variant="caption" color="text.disabled">—</Typography>
                        }
                      </TableCell>
                      <TableCell>
                        {row.language
                          ? <LangFlag code={row.language} size={16} />
                          : <Typography variant="caption" color="text.disabled">—</Typography>
                        }
                      </TableCell>
                      <TableCell>
                        {row.level
                          ? <Chip label={row.level} size="small" />
                          : <Typography variant="caption" color="text.disabled">—</Typography>
                        }
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          onClick={phase === 'review' ? () => setPhase('upload') : onClose}
          disabled={applying}
        >
          {phase === 'review' ? t('common.back') : t('common.close')}
        </Button>
        {phase === 'review' && (
          <Button
            variant="contained"
            onClick={handleApply}
            disabled={applying || selectedCount === 0}
            startIcon={applying ? <CircularProgress size={16} /> : <CheckIcon />}
          >
            {applying
              ? t('queryManager.import.applying')
              : t('queryManager.import.addCount', { count: selectedCount })}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
